/**
 * @providesModule DepositOffersGrid
 */

import {OfferInfo, PromoInfo, StoreOfferInfo} from 'app/ObjectModel/CheckoutFlow/StoreInfo';
import {TelemetryEventName} from 'TelemetryConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {DEPOSIT_TAP_INSUFFICIENT_FUND_MODAL, DEPOSIT_TAP_STORE} from 'AWSConstants';
import AWSManager from 'AWSManager';
import CheckoutController from 'CheckoutController';
import ClientPropertyConstants from 'ClientPropertyConstants';
import DepositOfferTile from 'DepositOfferTile';
import I18n, {_m} from 'INTL';
import React from 'react';
import {ActivityIndicator, View} from 'react-native';
import {Navigator} from 'react-native-deprecated-custom-components';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import styles from 'VirtualCurrencyOffersGridStyle';

const {textColorPrimary} = SkillzStyle.sheetStyles;

const HORIZONTAL_MARGIN = Sizes.select({
  phone: 4,
  tablet: 8,
});
interface DepositOffersGridProps {
  loaded: boolean;
  mainNavigator: Navigator;
  offersBoxWidth: number;
  onLayout: () => void;
  promo: PromoInfo;
  storeResponse: StoreOfferInfo;
  maxOffersToDisplay: number;
  onSelectOffer?: (() => void) | null;
}

export default class DepositOffersGrid extends React.Component<DepositOffersGridProps> {
  selectOffer(storeInfo: StoreOfferInfo, index: number, promo: PromoInfo) {
    if (CheckoutController.isLaunchingCheckout()) {
      return;
    }
    if (this.props.onSelectOffer !== null && this.props.onSelectOffer !== undefined) {
      this.props.onSelectOffer();
      AWSManager.trackEvent(DEPOSIT_TAP_INSUFFICIENT_FUND_MODAL);
    } else {
      AWSManager.trackEvent(DEPOSIT_TAP_STORE);
    }

    const selectedOffer = storeInfo?.offers?.[index];
    const depositAmounts = storeInfo?.offers?.map((offer: any) => Number(offer?.amount ?? 0));
    const maxDepositSlateAmount = Math.max(...depositAmounts);
    const minDepositSlateAmount = Math.min(...depositAmounts);
    trackTelemetryEvent({
      event: TelemetryEventName.STORE_PAGE_DEPOSIT_SLATE_CLICK,
      payload: {
        deposit_slate_position: index + 1,
        deposit_slate_amount: selectedOffer?.amount,
        max_deposit_slate_amount: maxDepositSlateAmount,
        min_deposit_slate_amount: minDepositSlateAmount,
        deposit_slate_label: selectedOffer?.custom_label,
        bonus_value: selectedOffer?.bonus,
        deposit_slate_id: selectedOffer?.id,
      },
      includeUserWalletDetails: true,
    });

    CheckoutController.launchCheckoutFlow({
      storeInfo,
      offer: storeInfo.offers[index],
      promo,
      navigator: this.props.mainNavigator,
    });
  }

  _renderLoadingView(): React.ReactNode {
    return (
      <View style={[styles.container, {alignItems: 'center', flex: 1, flexGrow: 1}]}>
        <ActivityIndicator color={textColorPrimary.color} size={'small'} />
      </View>
    );
  }

  _renderOffers(): React.ReactNode {
    if (!this.props.loaded) {
      return this._renderLoadingView();
    }

    const result: React.ReactNode[] = [];
    const offersResponse = this.props.storeResponse.offers;
    const rows: React.ReactNode[] = [];

    if (Array.isArray(offersResponse)) {
      // Get threshold value for filtering
      const cashThreshold = ClientPropertyConstants.getReferralNewUiStoreCashThreshold();
      
      // Filter offers first, then render
      let filteredOffers = offersResponse.filter((offer: OfferInfo, index: number) => {
        // Skip if we've reached max display limit
        if (index >= this.props.maxOffersToDisplay) {
          return false;
        }
        
        // Skip $5 template when referral promo code is applied
        if (this.props.promo?.is_referral_promo_code && offer.amount <= cashThreshold) {
          return false;
        }
        
        return true;
      });

      // Edge case: If no offers remain after filtering, show the highest offer
      if (filteredOffers.length === 0 && offersResponse.length > 0) {
        const highestOffer = offersResponse.reduce((max: OfferInfo, current: OfferInfo) => {
          return current.amount > max.amount ? current : max;
        });
        filteredOffers = [highestOffer];
      }

      filteredOffers.forEach((offer: OfferInfo, filteredIndex: number, filteredArray: OfferInfo[]) => {
        const isWide = filteredArray.length % 2 && filteredIndex === filteredArray.length - 1;
        let subTitleText: string | null = '';
        if (offer.bonus || this.props.promo) {
          const amount =
            (offer.bonus || 0) + (CheckoutController.getPromoAmount(offer, this.props.promo) || 0);
          subTitleText = I18n.getCurrencyString(amount, '+$', true, false) + ' ' + _m('BONUS');
        } else {
          subTitleText = null;
        }
        const width = this.props.offersBoxWidth / (isWide ? 1 : 2) - HORIZONTAL_MARGIN * 2;
        
        // Find original index for selectOffer call
        const originalIndex = offersResponse.findIndex(originalOffer => originalOffer.id === offer.id);
        
        if (offer) {
          result.push(
            <DepositOfferTile
              index={filteredIndex}
              isBestOffer={offer.best_value}
              isExtendedWidth={Boolean(isWide)}
              key={offer.id}
              onChange={() => {
                this.selectOffer(this.props.storeResponse, originalIndex, this.props.promo);
              }}
              primaryText={I18n.getCurrencyString(offer.amount, '$', true, false)}
              subText={subTitleText}
              width={width}
            />
          );
          if (result.length === 2 || isWide) {
            rows.push(
              <View key={`row-${filteredIndex}`} style={styles.rowStyle}>
                {Array.from(result)}
              </View>
            );
            result.length = 0;
          }
        }
      });
    }
    return rows;
  }

  render(): React.ReactNode {
    return (
      <View onLayout={this.props.onLayout} style={styles.offersBox}>
        {this._renderOffers()}
      </View>
    );
  }
}
